// Wire-shape requests for /articles/:slug/annotations + /annotations/:id.
import { z } from 'zod';
import { AnnotationScope, AnnotationType } from './annotation.js';
const HEX_COLOR_PATTERN = /^#(?:[0-9a-fA-F]{3}){1,2}(?:[0-9a-fA-F]{2})?$/;
// ---------------------------------------------------------------------------
// CreateAnnotationRequest — POST /articles/:slug/annotations.
//
// Aligns with the editor's `Comment` shape so the frontend can pipe
// new comments straight from `onCommentsChange` after diffing against
// the previous list. The editor produces `createdAt` as ms; the
// frontend converts to ISO before POSTing (or omits and lets the
// server stamp it).
// ---------------------------------------------------------------------------
export const CreateAnnotationRequest = z.object({
    anchorBlockId: z.string().min(1).max(128),
    anchorOffset: z.number().int().min(0),
    anchorLength: z.number().int().min(0),
    content: z.string().max(4000).optional(),
    color: z.string().regex(HEX_COLOR_PATTERN).optional(),
    type: AnnotationType.optional(),
    /** Required when `type === 'deletion'`; the editor populates it
     *  from the original text under the deletion's anchor. */
    deletedContent: z.string().max(4000).nullable().optional(),
    scope: AnnotationScope.optional(),
    /** Group ids to attach on create. Ignored unless `scope === 'group'`. */
    groupIds: z.array(z.string().uuid()).max(64).optional(),
});
// ---------------------------------------------------------------------------
// UpdateAnnotationRequest — PATCH /annotations/:id.
//
// Anchor fields are immutable post-create. Mutating an anchor would
// invalidate the editor's highlight map; the editor's correct path is
// "delete + recreate" via two separate calls.
//
// Type and scope CAN be patched (e.g. promote a comment to a tracked
// change, or escalate scope private → public).
// ---------------------------------------------------------------------------
export const UpdateAnnotationRequest = z
    .object({
    content: z.string().max(4000),
    color: z.string().regex(HEX_COLOR_PATTERN),
    type: AnnotationType,
    deletedContent: z.string().max(4000).nullable(),
    scope: AnnotationScope,
    groupIds: z.array(z.string().uuid()).max(64),
})
    .partial();
// ---------------------------------------------------------------------------
// ListAnnotationsQuery — GET /articles/:slug/annotations.
// ---------------------------------------------------------------------------
export const ListAnnotationsQuery = z.object({
    /** Filter by anchor block id — frontend uses this to fetch only the
     *  visible-block window on long articles. Optional; default = all
     *  active annotations. */
    blockId: z.string().min(1).max(128).optional(),
    /** Filter by resolution status. Default = both. */
    resolved: z.boolean().optional(),
    /** Filter by type. Default = all. */
    type: AnnotationType.optional(),
    /** Include archived (soft-deleted). Default = false. */
    includeArchived: z.boolean().optional(),
    limit: z.number().int().min(1).max(500).optional(),
    offset: z.number().int().min(0).optional(),
});
// Empty bodies — keep the type around so handlers can type-narrow.
export const ResolveAnnotationRequest = z.object({}).strict();
//# sourceMappingURL=requests.js.map